import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import Moment from 'react-moment';
import { useTranslation } from 'react-i18next';

import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import { PageTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Breakpoint from 'packages/kakapo-ui-kit/helpers/Breakpoint';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import OptIn from 'packages/kakapo-ui-application/pages/learning/components/OptInButton';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import LearningEventOccupancyWarning from './LearningEventOccupancyWarning';
import { ButtonSize as PrimaryButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import OutlineButton from 'packages/kakapo-ui-kit/components/buttons/OutlineButton';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

const LearningEventHeaderBackdropContainer = styled.div`
	width: 100%;
	height: 100%;
	position: absolute;
	background-color: ${(props) => props.theme.colors.slateBlue};
`;

const LearningEventHeaderBackdrop = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
	z-index: 0;
`;

const LearningEventHeaderContent = styled(Flex)`
	position: relative;
	z-index: 1;
	background-image: linear-gradient(
		180deg,
		rgba(0, 0, 0, 0) 0%,
		rgba(0, 0, 0, 0.6) 33%,
		#000000 100%
	);
`;

const LearningEventHeaderElement = styled(Flex)`
	position: relative;
	min-height: 200px;
	@media screen and (min-width: ${Breakpoint.Medium}) {
		min-height: 300px;
	}
`;

const LearningEventHeader = (props: { event: graphql.LearningEvent }) => {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();

	const user = useAuthenticatedUser();

	const [join, { loading: joinLoading }] = graphql.useJoinLearningEventMutation({
		variables: {
			input: {
				attendeeType: graphql.LearningEventAttendeeType.Attendee,
				learningEventID: props.event.id,
				userID: user.id,
			},
		},
	});

	const [leave, { loading: leaveLoading }] = graphql.useLeaveLearningEventMutation({
		variables: {
			input: {
				learningEventID: props.event.id,
				userID: user.id,
			},
		},
	});

	const [review, { loading: reviewLoading }] = graphql.useReviewLearningEventMutation();

	const roles = useViewerRoles();

	const canReview =
		(roles.isWorkspaceSuperuser || roles.isLearningOfficer) && !props.event.isArchived;

	const isRejected =
		props.event.lastRejectedAt &&
		new Date(props.event.lastRejectedAt) >= new Date(Date.now() - 1 * 60 * 1000);

	const cancelled = props.event.status === graphql.LearningEventStatus.Cancelled;

	return (
		<LearningEventHeaderElement flexDirection="column" justifyContent="space-between">
			<LearningEventHeaderBackdropContainer>
				{props.event.cover?.source.url && (
					<LearningEventHeaderBackdrop src={props.event.cover?.source.url + '?d=600x400'} alt="" />
				)}
			</LearningEventHeaderBackdropContainer>
			{props.event.attendeeCount > 0.8 * props.event.capacity ? (
				<LearningEventOccupancyWarning
					count={props.event.attendeeCount}
					total={props.event.capacity}
				/>
			) : (
				<div />
			)}
			<LearningEventHeaderContent
				px={[2, 3, 4]}
				py={3}
				justifyContent="space-between"
				alignItems="center"
				flexWrap="wrap"
				flexGrow={0}
			>
				<Box mr={3}>
					<PageTitle color="white">{localize(props.event.title)?.text}</PageTitle>
					<Paragraph color="white">
						<Moment format="DD-MM-YYYY &mdash; HH:mm">{props.event.startDate}</Moment>
					</Paragraph>
				</Box>
				{!cancelled && (
					<Flex flexGrow={1} py={2} justifyContent="flex-end" alignItems="center">
						{props.event.status === graphql.LearningEventStatus.Published ? (
							<OptIn
								joined={props.event.isViewerAttending}
								disabled={false}
								loading={joinLoading || leaveLoading}
								onClick={async () => await (props.event.isViewerAttending ? leave() : join())}
							/>
						) : canReview ? (
							reviewLoading ? (
								<Box>
									<Spinner size="18px" isLoading verticalMargin={0} />
								</Box>
							) : (
								<>
									<OutlineButton
										size={PrimaryButtonSize.Small}
										onClick={async () => {
											await review({
												variables: { input: { id: props.event.id, approved: true } },
											});
										}}
									>
										{t('learning.event.modal.approveButton.title')}
									</OutlineButton>
									{isRejected ? null : (
										<>
											<Box ml={3} />
											<OutlineButton
												size={PrimaryButtonSize.Small}
												onClick={async () => {
													await review({
														variables: { input: { id: props.event.id, approved: false } },
													});
												}}
											>
												{t('learning.event.modal.rejectButton.title')}
											</OutlineButton>
										</>
									)}
								</>
							)
						) : null}
						{roles.isLearningOfficer ||
						roles.isWorkspaceSuperuser ||
						props.event.createdBy.id === user.id ? (
							<Box ml={3}>
								<PrimaryButton
									size={PrimaryButtonSize.Small}
									onClick={() => history.push({
										pathname: `/learning/event/${props.event.id}/edit`,
										state: {
											backgroundLocation: history.location.state?.backgroundLocation
										},
									})}
								>
									{t('learning.event.modal.editButton.title')}
								</PrimaryButton>
							</Box>
						) : null}
					</Flex>
				)}
			</LearningEventHeaderContent>
		</LearningEventHeaderElement>
	);
};

export default LearningEventHeader;
